import * as React from "react";
import type { UIMessage } from "ai";
import { cjk } from "@streamdown/cjk";
import { code } from "@streamdown/code";
import { math } from "@streamdown/math";
import { mermaid } from "@streamdown/mermaid";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Streamdown } from "streamdown";
import { cn } from "../lib/cn.js";

export interface MessageProps extends React.HTMLAttributes<HTMLDivElement> {
  from: UIMessage["role"];
}

/**
 * AI Elements Message, adapted from Vercel's registry component for Flint.
 */
export function Message({ className, from, ...props }: MessageProps) {
  return (
    <div
      className={cn(
        "group flex w-full flex-col gap-2",
        from === "user"
          ? "is-user ml-auto max-w-[78%] items-end justify-end"
          : "is-assistant max-w-full",
        className,
      )}
      {...props}
    />
  );
}

export type MessageContentProps = React.HTMLAttributes<HTMLDivElement>;

export function MessageContent({ children, className, ...props }: MessageContentProps) {
  return (
    <div
      className={cn(
        "flex w-fit min-w-0 max-w-full flex-col gap-2 overflow-hidden text-sm leading-6",
        "group-[.is-user]:ml-auto group-[.is-user]:rounded-xl group-[.is-user]:border group-[.is-user]:border-line group-[.is-user]:bg-paper group-[.is-user]:px-4 group-[.is-user]:py-3 group-[.is-user]:text-ink group-[.is-user]:shadow-sm",
        "group-[.is-assistant]:text-ink",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export type MessageResponseProps = React.ComponentProps<typeof Streamdown>;

const streamdownPlugins = { cjk, code, math, mermaid };

export const MessageResponse: React.NamedExoticComponent<MessageResponseProps> = React.memo(
  function MessageResponse({ className, controls, ...props }: MessageResponseProps) {
    return (
      <Streamdown
        className={cn("size-full [&>*:first-child]:mt-0 [&>*:last-child]:mb-0", className)}
        plugins={streamdownPlugins}
        // Streamdown ships with a rounded card + copy/download/expand
        // chrome around every table, code block, and mermaid diagram.
        // For Flint's chat surface we want bare markdown — plain
        // tables that scroll horizontally, plain `pre` blocks. The
        // caller can still opt back in by passing their own `controls`.
        controls={controls ?? false}
        {...props}
      />
    );
  },
);

export interface MessageToolProps extends React.HTMLAttributes<HTMLDivElement> {
  name: string;
  state?: string;
  output?: unknown;
}

export function MessageTool({
  name,
  state = "input-available",
  output,
  className,
  ...props
}: MessageToolProps) {
  const complete = state === "output-available";
  return (
    <div
      className={cn(
        "rounded-md border border-line bg-paper px-3 py-2 text-xs text-ink shadow-sm",
        className,
      )}
      {...props}
    >
      <div className="flex items-center gap-2 font-medium">
        {complete ? (
          <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
        ) : (
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        )}
        <span>{humanizeTool(name)}</span>
      </div>
      {output !== undefined ? (
        <pre className="mt-2 max-h-56 overflow-auto rounded bg-paper p-2 font-mono text-xs leading-5 text-ink">
          {JSON.stringify(output, null, 2)}
        </pre>
      ) : null}
    </div>
  );
}

function humanizeTool(name: string) {
  return name
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/^./, (char) => char.toUpperCase());
}
